/**
 * Cache service using WeakRef to allow garbage collection of values
 * when they're no longer referenced elsewhere and memory pressure occurs.
 * Uses string keys with weakly-referenced values.
 */
export class WeakCacheService {
  #cache: Map<string, WeakRef<any>>

  constructor() {
    this.#cache = new Map()
  }

  /**
   * Get a cached value by key
   * @param {string} key - String key to lookup
   * @returns {*} The cached value or undefined if not found or garbage collected
   */
  get(key: string) {
    const weakRef = this.#cache.get(key)

    if (!weakRef) {
      return null
    }

    const value = weakRef.deref()

    // If value was garbage collected, clean up the entry
    if (value === undefined) {
      this.#cache.delete(key)
      return null
    }

    return value
  }

  /**
   * Create/store a value in the cache with weak reference
   * @param {string} key - String key for the cache entry
   * @param {Object} value - Value to cache (must be an object, not a primitive)
   * @returns {Object} The cached value
   */
  create(key: string, value: unknown) {
    if (typeof value !== 'object' || value === null) {
      throw new Error('Cached values must be objects for WeakRef to work')
    }

    const weakRef = new WeakRef(value)
    this.#cache.set(key, weakRef)

    return value
  }

  /**
   * Check if a key exists and its value is still alive
   * @param {string} key - String key to check
   * @returns {boolean} True if key exists and value hasn't been garbage collected
   */
  has(key: string) {
    const weakRef = this.#cache.get(key)
    if (!weakRef) {
      return false
    }

    const value = weakRef.deref()

    if (value === undefined) {
      this.#cache.delete(key)
      return false
    }

    return true
  }

  /**
   * Manually delete a cache entry
   * @param {string} key - String key to delete
   * @returns {boolean} True if deletion was successful
   */
  delete(key: string) {
    return this.#cache.delete(key)
  }

  /**
   * Get the number of cache entries (including potentially garbage collected ones)
   * @returns {number} Cache size
   */
  size() {
    return this.#cache.size
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.#cache.clear()
  }
}

export const weakCacheService = new WeakCacheService()
